import React, { useId, useState } from 'react';
import { Form } from 'react-bootstrap';
import { candidacyService } from '@/shared/services/candidacyService';
import { PREVIEW_LOCK_NOTE_ID } from '@/shared/hooks/usePreviewLock';
import {
    RefusalAlert,
    SAVE_FALLBACK,
    WriteStatus,
    refusalOf,
} from './reviewerStages';

// The server accepts ranks from 1 to 999; the choices run to the number of
// candidates still in play, and further when a rank already set is higher.
const RANK_MAX = 999;

/**
 * The reviewers' ranking of one candidate, chosen from a list and saved as
 * soon as it changes. "Not ranked" clears it. `count` is how many candidates
 * the position has in play.
 */
export default function RankSelect({
    candidacy,
    mode,
    count,
    disabled,
    onChange,
    onReload,
}) {
    const id = useId();
    const [busy, setBusy] = useState(false);
    const [refusal, setRefusal] = useState(null);
    const [notice, setNotice] = useState('');
    const current = candidacy.positionRank;
    const last = Math.min(
        RANK_MAX,
        Math.max(count || 1, Number.isInteger(current) ? current : 0)
    );
    const choices = Array.from({ length: last }, (_, i) => i + 1);
    const name = candidacy.student?.name || 'this student';
    const label = mode === 'coordinator' ? 'Ranking' : 'Your ranking';

    const save = async (value) => {
        const positionRank = value === '' ? null : Number(value);
        setBusy(true);
        setRefusal(null);
        setNotice('');
        try {
            const updated = await candidacyService.setRank(
                candidacy.id,
                positionRank
            );
            const saved = updated?.positionRank ?? null;
            const text =
                saved == null
                    ? `Ranking cleared for ${name}.`
                    : `${name} ranked ${saved}.`;
            setNotice(text);
            onChange?.(updated, text);
        } catch (err) {
            setRefusal(refusalOf(err, SAVE_FALLBACK));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div>
            <div className="d-flex align-items-center gap-2">
                <Form.Label
                    htmlFor={`${id}-rank`}
                    className="mb-0 small text-nowrap"
                >
                    {label}
                </Form.Label>
                <Form.Select
                    id={`${id}-rank`}
                    className="w-auto"
                    value={Number.isInteger(current) ? String(current) : ''}
                    disabled={disabled || busy}
                    aria-describedby={
                        disabled ? PREVIEW_LOCK_NOTE_ID : undefined
                    }
                    onChange={(e) => save(e.target.value)}
                >
                    <option value="">Not ranked</option>
                    {choices.map((n) => (
                        <option key={n} value={String(n)}>
                            {n}
                        </option>
                    ))}
                </Form.Select>
            </div>
            {/* The saved rank shows in the list, so its notice is for a
                screen reader only. */}
            <div className="visually-hidden">
                <WriteStatus refusal={null} notice={notice} />
            </div>
            {refusal && <RefusalAlert refusal={refusal} onReload={onReload} />}
        </div>
    );
}
